import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { X, Receipt, Wallet } from 'lucide-react';
import BuyHistoryModal from './BuyHistoryModal';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import CountrySelect from './CountrySelect';
import api from '../utils/axiosConfig';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import CoachOfferingsEditor from './coaches/CoachOfferingsEditor';

const THEME_OPTIONS = [
  { value: 'light-blue', label: 'Light Blue' },
  { value: 'light-slate', label: 'Light Slate' },
  { value: 'light-orange', label: 'Light Orange' },
  { value: 'light-green', label: 'Light Green' },
  { value: 'light-rose', label: 'Light Rose' },
  { value: 'light-violet', label: 'Light Violet' },
  { value: 'dark-blue', label: 'Dark Blue' },
  { value: 'dark-slate', label: 'Dark Slate' },
  { value: 'dark-orange', label: 'Dark Orange' },
  { value: 'dark-green', label: 'Dark Green' },
  { value: 'dark-rose', label: 'Dark Rose' },
  { value: 'dark-violet', label: 'Dark Violet' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'no', label: 'Norsk' },
];

export default function SettingsModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const { user, updateProfile } = useAuth();
  const [buyHistoryOpen, setBuyHistoryOpen] = useState(false);
  const {
    theme,
    language,
    maxHeartrate,
    weightKg,
    runningCriticalPowerWatts,
    distanceUnits,
    zitiusHealthSyncConsent,
    updateSetting,
    fetchSettings,
  } = useSettings();
  const overlayRef = useRef(null);
  const isCoach = !!user?.roles?.includes('coach');
  const isAthlete = !!user?.roles?.includes('athlete');

  const patchHealthConsent = (partial) => {
    const prev = zitiusHealthSyncConsent || {};
    const next = {
      ...prev,
      ...partial,
      consentVersion: 1,
    };
    if (partial.enabled === true) {
      next.consentedAt = new Date().toISOString();
    }
    if (partial.enabled === false) {
      next.sleep = false;
      next.vitals = false;
    }
    updateSetting('zitiusHealthSyncConsent', next);
  };
  const [coachBio, setCoachBio] = useState('');
  const [coachHeadline, setCoachHeadline] = useState('');
  const [coachListed, setCoachListed] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachSaving, setCoachSaving] = useState(false);
  const [coachMessage, setCoachMessage] = useState(null);

  useEffect(() => {
    if (!isOpen || !isCoach) return;
    let cancelled = false;
    setCoachLoading(true);
    setCoachMessage(null);
    (async () => {
      try {
        const profileRes = await api.get('/api/coaches/me/profile');
        if (cancelled) return;
        const p = profileRes.data?.profile;
        if (p) {
          setCoachBio(p.bio || '');
          setCoachHeadline(p.headline || '');
          setCoachListed(!!p.is_listed);
        }
      } catch {
        if (!cancelled) setCoachMessage({ type: 'error', text: t('coaches.coachProfileError') });
      } finally {
        if (!cancelled) setCoachLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isCoach, t]);

  const saveCoachProfile = async () => {
    setCoachSaving(true);
    setCoachMessage(null);
    try {
      await api.put('/api/coaches/me/profile', {
        bio: coachBio,
        headline: coachHeadline || null,
        isListed: coachListed,
      });
      setCoachMessage({ type: 'ok', text: t('coaches.coachProfileSaved') });
    } catch (e) {
      setCoachMessage({
        type: 'error',
        text: e.response?.data?.error || t('coaches.coachProfileError'),
      });
    } finally {
      setCoachSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchSettings();
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, fetchSettings]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className='fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200'
      style={{ zIndex: 10000 }}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        className='bg-card border border-border rounded-xl shadow-2xl flex flex-col animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 max-w-[90vw] w-[560px] max-h-[90vh]'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between px-5 py-4 border-b border-border shrink-0'>
          <h2 className='text-base font-semibold text-foreground'>
            {t('nav.settings')}
          </h2>
          <Button variant='ghost' size='icon' className='h-8 w-8' onClick={onClose}>
            <X className='h-4 w-4' />
          </Button>
        </div>

        <div className='p-5 overflow-y-auto min-h-0'>
          <div className='grid grid-cols-2 gap-x-6 gap-y-6'>
          <div className='col-span-2'>
            <Button
              variant='outline'
              className='w-full justify-start gap-2'
              onClick={() => setBuyHistoryOpen(true)}
            >
              <Receipt className='h-4 w-4' />
              {t('settings.buyHistory')}
            </Button>
          </div>

          {isCoach && (
            <div className='col-span-2'>
              <Button variant='outline' className='w-full justify-start gap-2' asChild>
                <Link to='/connect' onClick={onClose}>
                  <Wallet className='h-4 w-4' />
                  {t('nav.payouts')}
                </Link>
              </Button>
            </div>
          )}

          <div className='space-y-2'>
            <Label className='text-sm font-medium'>{t('settings.country')}</Label>
            <CountrySelect
              value={user?.countryId ?? null}
              onChange={(id) => updateProfile({ countryId: id })}
              placeholder={t('settings.country')}
              popoverClassName='z-[10001]'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='birthdate' className='text-sm font-medium'>
              {t('settings.birthdate')}
            </Label>
            <Input
              id='birthdate'
              type='date'
              value={user?.birthdate ?? ''}
              onChange={(e) => updateProfile({ birthdate: e.target.value || null })}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='max-heartrate' className='text-sm font-medium'>
              {t('settings.maxHeartrate')}
            </Label>
            <Input
              id='max-heartrate'
              type='number'
              min={40}
              max={250}
              placeholder='e.g. 185'
              value={maxHeartrate ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                updateSetting('maxHeartrate', v === '' ? null : (parseInt(v, 10) || null));
              }}
            />
            <p className='text-xs text-muted-foreground'>
              {t('settings.maxHeartrateDesc')}
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='weight-kg' className='text-sm font-medium'>
              {t('settings.weightKg')}
            </Label>
            <Input
              id='weight-kg'
              type='number'
              min={30}
              max={200}
              step={0.1}
              placeholder='e.g. 70'
              value={weightKg ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                updateSetting('weightKg', v === '' ? null : (parseFloat(v) || null));
              }}
            />
            <p className='text-xs text-muted-foreground'>
              {t('settings.weightKgDesc')}
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='running-cp-w' className='text-sm font-medium'>
              {t('settings.runningCriticalPower')}
            </Label>
            <Input
              id='running-cp-w'
              type='number'
              min={50}
              max={800}
              step={1}
              placeholder='e.g. 343'
              value={runningCriticalPowerWatts ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                updateSetting('runningCriticalPowerWatts', v === '' ? null : parseFloat(v) || null);
              }}
            />
            <p className='text-xs text-muted-foreground'>{t('settings.runningCriticalPowerDesc')}</p>
          </div>

          <div className='space-y-2'>
            <Label className='text-sm font-medium'>{t('settings.distanceUnits')}</Label>
            <Select
              value={distanceUnits === 'miles' ? 'miles' : 'km'}
              onValueChange={(v) => updateSetting('distanceUnits', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='z-[10001]'>
                <SelectItem value='km'>{t('settings.distanceUnitsKm')}</SelectItem>
                <SelectItem value='miles'>{t('settings.distanceUnitsMiles')}</SelectItem>
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>{t('settings.distanceUnitsDesc')}</p>
          </div>

          <div className='space-y-2'>
            <Label className='text-sm font-medium'>{t('settings.theme')}</Label>
            <Select
              value={theme || 'light-blue'}
              onValueChange={(v) => updateSetting('theme', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='z-[10001]'>
                {THEME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label className='text-sm font-medium'>{t('settings.language')}</Label>
            <Select
              value={language || 'en'}
              onValueChange={(v) => updateSetting('language', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='z-[10001]'>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isAthlete && (
            <div className='col-span-2 space-y-4 rounded-lg border border-border bg-muted/20 p-4'>
              <div>
                <h3 className='text-sm font-semibold text-foreground'>{t('settings.healthSyncTitle')}</h3>
                <p className='text-xs text-muted-foreground mt-1'>{t('settings.healthSyncHint')}</p>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <Label htmlFor='health-sync-master' className='text-sm cursor-pointer'>
                  {t('settings.healthSyncMaster')}
                </Label>
                <Switch
                  id='health-sync-master'
                  checked={!!zitiusHealthSyncConsent?.enabled}
                  onCheckedChange={(v) => patchHealthConsent({ enabled: v })}
                />
              </div>
              <div className='flex items-center justify-between gap-3 opacity-100'>
                <Label htmlFor='health-sync-sleep' className='text-sm cursor-pointer'>
                  {t('settings.healthSyncSleep')}
                </Label>
                <Switch
                  id='health-sync-sleep'
                  disabled={!zitiusHealthSyncConsent?.enabled}
                  checked={!!zitiusHealthSyncConsent?.sleep}
                  onCheckedChange={(v) => patchHealthConsent({ sleep: v })}
                />
              </div>
              <div className='flex items-center justify-between gap-3'>
                <Label htmlFor='health-sync-vitals' className='text-sm cursor-pointer'>
                  {t('settings.healthSyncVitals')}
                </Label>
                <Switch
                  id='health-sync-vitals'
                  disabled={!zitiusHealthSyncConsent?.enabled}
                  checked={!!zitiusHealthSyncConsent?.vitals}
                  onCheckedChange={(v) => patchHealthConsent({ vitals: v })}
                />
              </div>
            </div>
          )}
          </div>

          {isCoach && (
            <>
              <Separator className='my-6' />
              <div className='space-y-4'>
                <div>
                  <h3 className='text-sm font-semibold text-foreground'>{t('coaches.settingsTitle')}</h3>
                  <p className='text-xs text-muted-foreground mt-1'>{t('coaches.settingsHint')}</p>
                </div>
                {coachLoading ? (
                  <p className='text-sm text-muted-foreground'>…</p>
                ) : (
                  <>
                    <div className='space-y-2'>
                      <Label htmlFor='coach-headline'>{t('coaches.headline')}</Label>
                      <Input
                        id='coach-headline'
                        value={coachHeadline}
                        onChange={(e) => setCoachHeadline(e.target.value)}
                        placeholder={t('coaches.headlinePlaceholder')}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='coach-bio'>{t('coaches.bio')}</Label>
                      <Textarea
                        id='coach-bio'
                        rows={5}
                        value={coachBio}
                        onChange={(e) => setCoachBio(e.target.value)}
                        placeholder={t('coaches.bioPlaceholder')}
                        className='resize-y min-h-[100px]'
                      />
                    </div>
                    <div className='flex items-center justify-between gap-4 rounded-lg border border-border p-3'>
                      <Label htmlFor='coach-listed' className='cursor-pointer'>
                        {t('coaches.listedInCatalog')}
                      </Label>
                      <Switch
                        id='coach-listed'
                        checked={coachListed}
                        onCheckedChange={setCoachListed}
                      />
                    </div>
                    <Separator className='my-4' />
                    <CoachOfferingsEditor disabled={coachSaving} />
                    {coachMessage && (
                      <p
                        className={
                          coachMessage.type === 'ok' ? 'text-sm text-green-600 dark:text-green-500' : 'text-sm text-destructive'
                        }
                        role='status'
                      >
                        {coachMessage.text}
                      </p>
                    )}
                    <Button type='button' onClick={saveCoachProfile} disabled={coachSaving}>
                      {coachSaving ? '…' : t('coaches.saveCoachProfile')}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <BuyHistoryModal isOpen={buyHistoryOpen} onClose={() => setBuyHistoryOpen(false)} />
    </div>,
    document.body
  );
}
